import type { IDataTable, IRenderKey } from "@helium10/re-ui-components";
import { baseFetch } from "@helium10/re-core";
import { DocumentCenterLink } from "@/core/apiLink";

/**
 * 列表页请求参数
 */
export interface IListParams {
  accountId: string;
  pageNum: number;
  pageSize: number;
  orderBy: "startTime" | "endTime" | "";
  orderType: "asc" | "desc";
  createStart: string;
  createEnd: string;
}

/**
 * 列表页返回数据项
 */
export interface IUploadHistoryItem extends IRenderKey, IDataTable {
  batchId: string;
  fileName: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  itemTotalCount: number;
  itemSuccessCount: number;
  itemFailCount: number;
  uploadStatus: 0 | 1 | 2 | 3 | 4 | 6; // 0 init, 1 uploading, 2 success, 3 failed, 4 partial success/failed, 6 processing
  createBy: string;
  startTime: string;
  endTime: string;
  startTimeFormat: string;
  endTimeFormat: string;
  shipmentIds: string;
  shipmentIdList: string[];
}

/**
 * 列表页返回数据
 */
export interface IListResponse {
  code: number;
  message: string | null;
  data: {
    totalCount: number;
    data: IUploadHistoryItem[];
    totalData: null;
  };
  success: boolean;
}

/**
 * 详情页请求参数
 */
export interface IDetailParams {
  accountId: string;
  batchId: string;
}

/**
 * 详情页文件项
 */
export interface IFileItem {
  fileHash: string;
  fileSize: number;
  filePath: string;
  fileName: string;
  ext: string;
  storagePath: string;
}

/**
 * 上传失败原因（originReason 原始值，同时作为 i18n 的 key）
 */
export const UPLOAD_FAILURE_REASONS = [
  "Upload interrupted",
  "System failed",
  "Total file size exceeds limit",
  "Single file size exceeds limit",
  "Shipment ID Out Of Scope",
  "Shipment ID Not Found",
  "The number of proof files uploaded for the same shipment ID exceeds the limit.",
  "Optical Character Recognition failed",
  "Optical Character Recognition timed out",
] as const;

export type UploadFailureReason = (typeof UPLOAD_FAILURE_REASONS)[number];

/**
 * 详情页成功/失败项
 */
export interface IDetailItem extends IRenderKey, IDataTable {
  filePath: string;
  shipmentId: string;
  originReason: string | null;
  reason: string | null;
  files: IFileItem[];
}

/**
 * 详情页返回数据
 */
export interface IDetailResponse {
  code: number;
  message: string | null;
  data: {
    batchId: string;
    total: number;
    successTotal: number;
    failedTotal: number;
    successList: IDetailItem[];
    failedList: IDetailItem[];
  };
  success: boolean;
}

/**
 * 获取上传历史列表
 */
export const getUploadHistory = async (params: IListParams) => {
  return baseFetch<IListParams, IListResponse>({
    method: "POST",
    url: DocumentCenterLink.uploadHistoryList,
    useSessionAccountId: true,
    params,
  });
};

/**
 * 获取上传历史详情
 */
export const getUploadHistoryDetail = async (params: IDetailParams) => {
  return baseFetch<IDetailParams, IDetailResponse>({
    method: "POST",
    url: DocumentCenterLink.uploadHistoryDetail,
    useSessionAccountId: true,
    params,
  });
};
